import { CreateBookingResponse } from '../hooks/useScheduling';
import { Card, CardContent } from '@travellegacy/nexops-design-system';
import { CheckCircle2, Info } from 'lucide-react';

interface SuccessScreenProps {
  bookingData?: CreateBookingResponse['data'];
  clientName: string;
  // Para agendamentos já processados
  scheduledAt?: string | null;
  alreadyScheduled?: boolean;
}

export function SuccessScreen({
  bookingData,
  clientName,
  scheduledAt,
  alreadyScheduled = false,
}: SuccessScreenProps) {
  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    const weekday = date.toLocaleDateString('pt-BR', { weekday: 'long' });
    const dateFormatted = date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
    return {
      date: `${weekday}, ${dateFormatted}`,
      time: date.toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
      }),
    };
  };

  // Usar scheduledAt se for agendamento já processado, senão usar bookingData
  const dateTime = scheduledAt
    ? formatDateTime(scheduledAt)
    : bookingData
      ? formatDateTime(bookingData.startTime)
      : null;

  if (!dateTime) return null;

  const { date, time } = dateTime;

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2] dark:bg-[#0f0606] p-4">
      <Card className="w-full max-w-md">
        <CardContent className="pt-6">
          <div className="text-center">
            {/* Success Icon */}
            <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>

            {/* Title */}
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              Onboarding Agendado!
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {clientName}, sua reunião de onboarding foi agendada com sucesso!
            </p>

            {/* Schedule Details */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 p-6 rounded-lg mb-6">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Data do agendamento:</p>
              <p className="font-bold text-lg text-gray-800 dark:text-gray-200 capitalize">
                {date}
              </p>
              <p className="text-3xl font-bold text-green-700 dark:text-green-400 mt-2">
                às {time}
              </p>
            </div>

            {/* Additional Info */}
            <div className="bg-[#DED1BD]/30 dark:bg-[#B05D41]/10 border border-[#B05D41]/30 dark:border-[#B05D41]/30 p-4 rounded-lg mb-6 text-left">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-[#B05D41] dark:text-[#B05D41] flex-shrink-0 mt-0.5" />
                <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                  <p>
                    • {alreadyScheduled ? 'Você já recebeu' : 'Você receberá'} um email de
                    confirmação do Google Calendar
                  </p>
                  <p>• Um lembrete será enviado via WhatsApp</p>
                  <p>• O evento já foi adicionado ao seu calendário</p>
                  <p>• Caso precise reagendar, entre em contato com nossa equipe</p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <p className="mt-6 text-sm text-gray-500 dark:text-gray-400">
              Pode acontecer do evento não adicionar no seu calendário automaticamente por não
              reconhecer o remetente, mas você recebe a confirmação! Nos vemos na call! 🚀
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
